/**
 * USE WHATSAPP SESSION POLLING — tiene aggiornata la chat WhatsApp finché
 * l'accordion che la contiene è aperto, senza ricaricare la pagina.
 *
 * L'aggancio al ciclo di vita è volutamente implicito: WhatsAppSessionChat viene
 * istanziato SOLO da accordion espanso (ActivityTimeline monta il body dentro una
 * guardia `expandedIds.has(item.id)`), quindi mount = pannello aperto e unmount =
 * pannello chiuso. Non serve sollevare `expandedIds` né aggiungere una callback di
 * chiusura al contratto della timeline: basta montare questo hook nella chat.
 *
 * L'apertura dell'accordion fa scattare subito un fetch: i messaggi arrivati mentre
 * il pannello era chiuso si vedono all'istante, e solo da lì parte la cadenza.
 *
 * IL POLL È PER CONTATTO, NON PER SESSIONE. L'endpoint dietro
 * `fetchWhatsAppSessions` è `/whatsapp-sessions/{groupId}/{personId}` e ritorna
 * TUTTE le sessioni del contatto coi messaggi annidati: con tre accordion aperti
 * sullo stesso contatto, tre timer chiamerebbero lo stesso URL sovrascrivendosi a
 * vicenda. Il registro qui sotto tiene un solo timer per personId, con conteggio dei
 * sottoscrittori: N chat aperte dello stesso contatto = 1 richiesta per tick, che le
 * aggiorna tutte insieme.
 *
 * Il ricarico usa lo stesso thunk del caricamento iniziale e del refresh post-invio
 * (con `silent: true`, così non muove status/error della slice): un solo modo di
 * ricaricare le sessioni, non tre.
 */

import { useEffect } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { fetchWhatsAppSessions } from './whatsAppSessionThunks';
import type { WhatsAppSession } from './whatsAppSessionTypes';

type AppDispatch = ReturnType<typeof useAppDispatch>;

const DEFAULT_INTERVAL_MS = 10000;
// Pavimento: un refuso in .env (es. un valore in secondi invece che in millisecondi)
// non deve poter trasformare il polling in una tempesta di richieste.
const MIN_INTERVAL_MS = 2000;

const POLL_INTERVAL_MS = (() => {
  const raw = import.meta.env.VITE_WHATSAPP_POLL_INTERVAL_MS;
  const parsed = raw === undefined ? NaN : Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_INTERVAL_MS;
  return Math.max(parsed, MIN_INTERVAL_MS);
})();

interface PollEntry {
  /** Quante chat aperte stanno chiedendo il polling per questo contatto. */
  subscribers: number;
  timerId: ReturnType<typeof setInterval>;
  /** Richiesta ancora in volo: il tick successivo si salta invece di accodarsi. */
  inFlight: boolean;
  onVisibilityChange: () => void;
}

// Stato puramente locale al ciclo di vita dei componenti montati: non va persistito,
// non lo legge nessun altro, non ha senso nei devtools. Nello store servirebbero
// azioni e reducer per una mappa di timer.
const registry = new Map<number, PollEntry>();

function poll(personId: number, dispatch: AppDispatch): void {
  const entry = registry.get(personId);
  if (!entry || entry.inFlight) return;

  // Scheda in background: i browser rallentano i timer ma non annullano le
  // richieste. Qui si salta del tutto; al rientro ci pensa onVisibilityChange.
  if (document.visibilityState === 'hidden') return;

  entry.inFlight = true;
  void Promise.resolve(
    dispatch(fetchWhatsAppSessions({ personId, silent: true })),
  ).finally(() => {
    // Rileggo dal registro: nel frattempo l'ultimo accordion può essersi chiuso.
    const current = registry.get(personId);
    if (current) current.inFlight = false;
  });
}

function subscribe(personId: number, dispatch: AppDispatch): void {
  const existing = registry.get(personId);

  if (existing) {
    existing.subscribers += 1;
  } else {
    // Rientro sulla scheda: poll immediato di recupero, così la chat è già
    // aggiornata quando l'utente la guarda invece di esserlo un tick dopo.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll(personId, dispatch);
    };

    registry.set(personId, {
      subscribers: 1,
      inFlight: false,
      timerId: setInterval(() => poll(personId, dispatch), POLL_INTERVAL_MS),
      onVisibilityChange,
    });

    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  // Aprire l'accordion vale come un tick: i messaggi arrivati mentre il pannello
  // era chiuso si vedono SUBITO, non dopo POLL_INTERVAL_MS. Vale anche per il
  // secondo accordion aperto sullo stesso contatto — la guardia `inFlight` in
  // poll() assorbe le aperture ravvicinate senza duplicare la richiesta.
  poll(personId, dispatch);
}

function unsubscribe(personId: number): void {
  const entry = registry.get(personId);
  if (!entry) return;

  entry.subscribers -= 1;
  if (entry.subscribers > 0) return;

  clearInterval(entry.timerId);
  document.removeEventListener('visibilitychange', entry.onVisibilityChange);
  registry.delete(personId);
}

/**
 * Attiva il polling dei nuovi messaggi finché il componente resta montato.
 *
 * Si attiva solo sulle sessioni NON scadute, cioè `windowState !== 'CLOSED'`:
 * - `OPEN` — il contatto ha scritto nelle ultime 24h, la conversazione è viva;
 * - `PENDING` — abbiamo scritto noi e stiamo aspettando la risposta: è proprio il
 *   caso per cui questo hook esiste;
 * - `CLOSED` — 24h scadute. Non può arrivare nulla in quella sessione: un eventuale
 *   ricontatto del contatto ne aprirebbe comunque una nuova.
 *
 * La condizione è rivalutata a ogni render, quindi una sessione che scade mentre
 * l'accordion è aperto smette di essere interrogata da sola.
 */
export function useWhatsAppSessionPolling(session: WhatsAppSession): void {
  const { personId, windowState } = session;
  useWhatsAppPersonPolling(personId, windowState !== 'CLOSED');
}

/**
 * Il polling di un contatto, senza guardare la finestra: lo usa la pagina WhatsApp
 * (FILO-37), dove la conversazione aperta deve vedere anche il contatto che
 * riscrive dopo 24h, cioè una sessione nuova. Stesso registro della scheda: un
 * timer per contatto, comunque lo si chieda.
 */
export function useWhatsAppPersonPolling(personId: number, enabled = true): void {
  const dispatch = useAppDispatch();
  const eligible = enabled && personId > 0;

  useEffect(() => {
    if (!eligible) return;
    subscribe(personId, dispatch);
    return () => unsubscribe(personId);
  }, [eligible, personId, dispatch]);
}
